import React from 'react';
import { Input } from '@roger/ui-components';
import type {
  GoalInterventionInstructionField,
  GoalLength,
} from '../services/api/types.gen';
import type { DisciplineType } from '../services/api/models/DisciplineType';

/**
 * Shared short/long-term radio options for goal create/modify/push forms.
 * Lives outside any component file so it can be imported without tripping
 * react-refresh's only-export-components rule.
 */
export const GOAL_TYPE_OPTIONS = [
  { value: 'short_term', label: 'Short term goal' },
  { value: 'long_term', label: 'Long term goal' },
];

/**
 * Disciplines that carry goals. MSW and HHA never do, so they are not
 * offered here even though the clinic-manager discipline list includes them
 * (EMR-1538/EMR-1661).
 */
export const GOAL_DISCIPLINE_OPTIONS: ReadonlyArray<{
  value: DisciplineType;
  label: string;
}> = [
  { value: 'SN', label: 'SN' },
  { value: 'PT', label: 'PT' },
  { value: 'OT', label: 'OT' },
  { value: 'ST', label: 'ST' },
] as const;

export const isGoalDiscipline = (
  discipline: DisciplineType | null | undefined
): discipline is DisciplineType =>
  GOAL_DISCIPLINE_OPTIONS.some(option => option.value === discipline);

/**
 * Renders instruction fields as a single React node that can be inserted into JSX.
 * - Fields with editable: false → displayed as normal text
 * - Fields with editable: true → displayed as greyed-out read-only text box with text as placeholder
 * Keys are sorted numerically for display order.
 */
export function renderInstructionFields(
  instruction: Record<string, GoalInterventionInstructionField>
): React.ReactNode {
  const entries = Object.entries(instruction).sort(
    ([a], [b]) => Number(a) - Number(b)
  );

  if (entries.length === 0) {
    return null;
  }

  return React.createElement(
    'div',
    { className: 'text-sm flex flex-wrap items-center gap-1 leading-relaxed' },
    ...entries.map(([key, field]) => {
      if (field.editable) {
        return React.createElement(Input, {
          key,
          placeholder: field.text,
          readOnly: true,
          className:
            'inline-block mx-1 h-6 px-2 text-sm bg-muted border-muted-foreground/30 cursor-not-allowed text-muted-foreground',
          style: {
            width: `${Math.max(field.text.length * 8 + 20, 80)}px`,
            verticalAlign: 'middle',
          },
        });
      }
      return React.createElement(
        'span',
        { key, className: 'mr-1' },
        field.text
      );
    })
  );
}

/**
 * Renders instruction fields as editable inputs that the user can fill in.
 * - Fields with editable: false → displayed as normal text
 * - Fields with editable: true → displayed as an active input with text as placeholder
 * Keys are sorted numerically for display order.
 */
export function renderEditableInstructionFields(
  instruction: Record<string, GoalInterventionInstructionField>,
  values: Record<string, string>,
  onChange: (key: string, value: string) => void
): React.ReactNode {
  const entries = Object.entries(instruction).sort(
    ([a], [b]) => Number(a) - Number(b)
  );

  if (entries.length === 0) {
    return null;
  }

  return React.createElement(
    'div',
    { className: 'text-sm flex flex-wrap items-center gap-1 leading-relaxed' },
    ...entries.map(([key, field]) => {
      if (field.editable) {
        return React.createElement(Input, {
          key,
          placeholder: field.text,
          value: values[key] ?? '',
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(key, e.target.value),
          className: 'inline-block mx-1 h-6 px-2 text-sm',
          style: {
            width: `${Math.max(field.text.length * 8 + 20, 80)}px`,
            verticalAlign: 'middle',
          },
        });
      }
      return React.createElement(
        'span',
        { key, className: 'mr-1' },
        field.text
      );
    })
  );
}

/** The re-target fields of a push: what the copy lands as in the next episode. */
export interface PushGoalFieldValues {
  instruction: string;
  length: GoalLength;
  targetDate: Date | undefined;
  isForEndOfEpisode: boolean;
}

/** A pushed copy needs an instruction (it prints on the plan of care) and a date. */
export const arePushGoalFieldsValid = (values: PushGoalFieldValues): boolean =>
  !!values.instruction.trim() && !!values.targetDate;
